import React, { useEffect, useState } from 'react'
import Keyboard from './Keyboard/Keyboard'
import { checkGuess, checkWord, getRandomWord } from '../utils/wordHelper'
import Guesses from './Guess/Guesses'
import NavHeader from './NavHeader'

type GameState = 'lost' | 'playing' | 'won'

const maxGuessCount = 6
const wordLength = 5
const successMessages = [
  'Amazing!',
  'Brilliant!',
  'Fantastic!',
  'Impressive!',
  'Masterful!',
  'You did it!',
]
const successEmojis = ['🤩', '🤓', '🎊', '🥳', '🏆', '🎉']

export const FLIP_DELAY = 200

type Props = {
  queryWord: string | null
}

const Wordle = ({ queryWord }: Props) => {
  const [word, setWord] = useState('')
  const [guesses, setGuesses] = useState<string[]>([])
  const [guessCount, setGuessCount] = useState(0)
  const [gameState, setGameState] = useState<GameState>('playing')

  const resetGame = (nextWord?: string | null) => {
    setGuesses(Array(maxGuessCount).fill(''))
    setGuessCount(0)
    setGameState('playing')

    const inputWord = nextWord ? atob(nextWord) : ''
    if (checkWord(inputWord.toLowerCase())) {
      setWord(inputWord.toUpperCase())
    } else {
      setWord(getRandomWord().toUpperCase())
    }
  }

  useEffect(() => {
    resetGame(queryWord)
  }, [queryWord])

  const addToCurrentGuess = (letter: string) => {
    console.log(word)
    if (gameState !== 'playing' || guesses[guessCount].length === wordLength) return

    const newGuess = guesses[guessCount] + letter
    setGuesses([...guesses.slice(0, guessCount), newGuess, ...guesses.slice(guessCount + 1)])
  }

  const removeFromCurrentGuess = () => {
    if (gameState !== 'playing' || guesses[guessCount].length === 0) return

    const newGuess = guesses[guessCount].slice(0, -1)
    setGuesses([...guesses.slice(0, guessCount), newGuess, ...guesses.slice(guessCount + 1)])
  }

  const copyToClipboard = (str: string) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText)
      return navigator.clipboard.writeText(str)
    return Promise.reject('The Clipboard API is not available.')
  }

  const handleReset = () => {
    resetGame()
  }

  const handleShare = async () => {
    const url = 'https://wordle-plus.vercel.app?word=' + btoa(word)
    const msg = `Wordle in ${guessCount}/${maxGuessCount} guesses!`
    const blocks = guesses.map((guess, i) => checkGuess(word, guess).join(''))
    await copyToClipboard(`${url}\n\n${msg}\n\n${blocks.join('\n')}`)
    alert('Copied to clipboard!')
  }

  const handleGuess = (guess: string) => {
    const newGuessCount = guessCount + 1
    setGuessCount(newGuessCount)

    setTimeout(() => {
      if (word === guess) {
        setGameState('won')
      } else if (newGuessCount === maxGuessCount) {
        setGameState('lost')
      }
    }, wordLength * FLIP_DELAY)
  }

  return (
    <>
      <NavHeader />
      <Guesses guesses={guesses} guessCount={guessCount} word={word} />

      {gameState === 'playing' && (
        <Keyboard
          key={word}
          guesses={guesses}
          guessCount={guessCount}
          word={word}
          onClickLetter={addToCurrentGuess}
          onClearLetter={removeFromCurrentGuess}
          onSubmitWord={handleGuess}
        />
      )}

      <div className="flex flex-col">
        {gameState === 'won' && (
          <span className="mt-8 text-3xl text-center text-white">
            {`${successMessages[Math.floor(Math.random() * successMessages.length)]}
              ${successEmojis[Math.floor(Math.random() * successEmojis.length)]}`}
          </span>
        )}
        {gameState === 'lost' && (
          <span className="mt-8 text-3xl text-center text-white">
            This was a tough one: {word}.<br />
            Try again! 💪
          </span>
        )}
        {gameState !== 'playing' && (
          <div className="flex flex-col">
            <button
              type="button"
              onClick={handleReset}
              className={
                'flex-shrink mx-auto bg-green-600 text-white rounded-md shadow-emerald-300 shadow-md text-xl w-32 h-12 mt-12'
              }
            >
              Play Again
            </button>
            <button
              type="button"
              onClick={handleShare}
              className={
                'flex-shrink mx-auto border border-solid border-slate-400 text-white rounded-md shadow-md text-xl w-32 h-12 mt-4'
              }
            >
              Share
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default Wordle
